// chartCache.jsx - shared LRU cache for ChartDirector chart responses so
// navigating to the next/previous script renders instantly instead of
// blanking the whole page and re-fetching.
//
// - One fetch serves BOTH ChartPanel (candles crop) and FundamentalsChart
//   (fundamentals crop), which previously downloaded the same SVG twice.
// - Neighbours of the selected row are pre-fetched in the background, so a
//   ‹ › / Next tap usually hits an already-loaded chart.
// - Keep-last behaviour: callers render the previous symbol's chart until
//   the new one arrives - no "Loading…" flash on every navigation.

const MAX_ENTRIES = 40;
const cache = new Map(); // key: `${link}|${period}` -> { svg, plot, candles, fetchedAt }
const inflight = new Map();

export function cacheKey(link, period, variant = 'full') {
  return `${link}|${period}|${variant}`;
}

export function peekCache(link, period, variant = 'full') {
  return cache.get(cacheKey(link, period, variant)) || null;
}

function store(key, value) {
  cache.set(key, value);
  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    // Never evict the entry we just added.
    if (oldest === key) break;
    cache.delete(oldest);
  }
}

export function fetchChart(link, period, variant = 'full') {
  const key = cacheKey(link, period, variant);
  const hit = cache.get(key);
  if (hit) {
    // Refresh LRU order.
    cache.delete(key);
    cache.set(key, hit);
    return Promise.resolve(hit);
  }
  if (inflight.has(key)) return inflight.get(key);

  // Full chart INCLUDING the fundamentals bars below the candles - the
  // ChartPanel shows the whole image, FundamentalsChart crops just the bars.
  // 'candles' variant = CD chart (candles only, fills its slot exactly like
  // bse3). 'full' variant = candles + fundamentals bars, cropped by
  // FundamentalsChart into the strip below.
  const query = period === 'daily' ? '?months=4' : '';
  const priceOnly = variant === 'candles' ? '&priceOnly=1' : '';
  const promise = Promise.all([
    apiGet(`/chart/${encodeURIComponent(link)}/${period}${query}${priceOnly}`),
    apiGet(`/candles/${encodeURIComponent(link)}/${period}`),
  ])
    .then(([chartBody, candleBody]) => {
      const value = {
        svg: chartBody.svg || '',
        plot: chartBody.plot || null,
        candles: Array.isArray(candleBody.candles) ? candleBody.candles : [],
        fetchedAt: Date.now(),
      };
      store(key, value);
      inflight.delete(key);
      return value;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });
  inflight.set(key, promise);
  return promise;
}

function apiGet(path) {
  return import('./api').then(({ apiFetch }) => apiFetch(path))
    .then((r) => {
      if (!r.ok) throw new Error(`request failed: ${r.status}`);
      return r.json();
    });
}

// Drop every cached chart for a link, so the next fetch shows changed candles
// (e.g. after a split adjustment) instead of the cached pre-change chart.
export function invalidateChart(link) {
  for (const key of [...cache.keys()]) {
    if (key.startsWith(`${link}|`)) cache.delete(key);
  }
}

export function prefetchChart(link, period = 'daily') {
  if (!link) return;
  fetchChart(link, period, 'candles').catch(() => {});
  fetchChart(link, period, 'full').catch(() => {});
}
